import { Kontraktsomrade, Riksvegrute } from '@/domain/omrader';
import {
    Relasjonstype,
    Vegobjekt,
    VegobjektEgenskap,
    VegobjektEgenskapEnhet,
    VegobjektLokasjon,
    VegobjektMetadata,
    VegobjektRelasjon,
    VegobjektRelasjoner,
    Vegsegment,
} from '@/domain/vegobjekter/Vegobjekt';
import { Detaljniva, DetaljnivaAdditional } from '@/middleware/queries';
import type {
    Egenskap as ApiEgenskap,
    Enhet as ApiEnhet,
    RelasjonMedVegobjekter as ApiRelasjonMedVegobjekter,
    Relasjoner as ApiRelasjoner,
    Vegobjekt as ApiVegobjekt,
    VegobjekterSide as ApiVegobjekterSide,
    Vegsegment as ApiVegsegment,
} from '@/generated/nvdb/vegobjekter/models';
import {
    VegobjektEgenskaperResponse,
    VegobjektResponse,
    VegobjektResponseMetadata,
} from '../responses';
import { transformGeometri, transformKvalitet } from './geometri';
import { transformVegobjektStedfesting } from './stedfesting';
import { transformVegsystemreferanse } from './vegsystemreferanse';

type ApiEgenskapResponse = Omit<ApiEgenskap, 'egenskapstype'> & {
    egenskapstype: string;
    stedfestingstype?: string;
    verdi?: string | number | boolean;
    enum_id?: number;
    href?: string;
    verifiseringsdato?: string;
    datafangstdato?: string;
    kvalitet?: Record<string, number>;
    enhet?: ApiEnhet;
    høydereferanse?: number;
};

type ApiVegobjektResponse = Omit<ApiVegobjekt, 'egenskaper'> & {
    egenskaper?: ApiEgenskapResponse[];
};

type ApiVegobjekterSideResponse = Omit<ApiVegobjekterSide, 'objekter'> & {
    objekter: ApiVegobjektResponse[];
};

export function transformVegobjektResponse(
    data: ApiVegobjekterSideResponse
): VegobjektResponse {
    return new VegobjektResponse(
        data.objekter.map(transformVegobjekt),
        new VegobjektResponseMetadata(
            data.metadata.returnert,
            data.metadata.neste,
            data.metadata.sidestørrelse
        )
    );
}

export function transformVegobjektEgenskaperResponse(
    data: ApiVegobjekterSideResponse
): VegobjektEgenskaperResponse {
    return new VegobjektEgenskaperResponse(
        data.objekter.map(objekt => ({
            id: objekt.id,
            egenskaper: transformEgenskaper(objekt.egenskaper),
        })),
        new VegobjektResponseMetadata(
            data.metadata.returnert,
            data.metadata.neste,
            data.metadata.sidestørrelse
        )
    );
}

export function transformVegobjektArray(
    objekter: Array<ApiVegobjektResponse | number>
): Vegobjekt[] {
    return objekter.map(data =>
        transformVegobjekt(
            typeof data === 'number'
                ? data
                : {
                      ...data,
                      lokasjon: data.lokasjon ?? {
                          kommuner: [],
                          fylker: [],
                          stedfestinger: [],
                          vegsystemreferanser: [],
                      },
                  }
        )
    );
}

export function transformVegobjekt(data: ApiVegobjektResponse | number): Vegobjekt {
    if (typeof data === 'number') {
        return Vegobjekt.dummy(data, null);
    }

    return new Vegobjekt(
        data.id,
        data.href,
        transformMetadata(data.metadata),
        transformEgenskaper(data.egenskaper),
        data.geometri ? transformGeometri(data.geometri) : null,
        transformLokasjon(data.lokasjon),
        transformRelasjoner(data.relasjoner),
        transformSegmenter(data.vegsegmenter)
    );
}

function transformMetadata(metadata: ApiVegobjekt['metadata']): VegobjektMetadata {
    if (!metadata) throw new Error('Vegobjekt mangler metadata');

    return new VegobjektMetadata(
        metadata.type,
        metadata.versjon,
        metadata.startdato,
        metadata.sist_modifisert,
        metadata.sluttdato
    );
}

function transformEgenskaper(
    egenskaper: ApiEgenskapResponse[] | undefined
): VegobjektEgenskap[] {
    return (
        egenskaper?.map(
            e =>
                new VegobjektEgenskap(
                    e.id,
                    e.navn,
                    e.egenskapstype,
                    e.stedfestingstype,
                    e.verdi,
                    e.enum_id,
                    e.href,
                    e.verifiseringsdato,
                    e.datafangstdato,
                    e.kvalitet ? transformKvalitet(e.kvalitet) : undefined,
                    e.enhet
                        ? new VegobjektEgenskapEnhet(
                              e.enhet.id,
                              e.enhet.navn ?? '',
                              e.enhet.kortnavn ?? ''
                          )
                        : undefined,
                    e.høydereferanse
                )
        ) ?? []
    );
}

function transformLokasjon(lokasjon: ApiVegobjekt['lokasjon']): VegobjektLokasjon {
    if (!lokasjon) {
        throw new Error('Vegobjekt mangler lokasjon');
    }

    return new VegobjektLokasjon(
        lokasjon.kommuner,
        lokasjon.fylker,
        lokasjon.kontraktsområder
            ? lokasjon['kontraktsområder'].map(ko => new Kontraktsomrade(ko.navn))
            : [],
        lokasjon.riksvegruter
            ? lokasjon.riksvegruter.map(r => new Riksvegrute(r.riksvegrute, r.enumid))
            : [],
        lokasjon.vegsystemreferanser
            ? lokasjon.vegsystemreferanser
                  .map(vsr => transformVegsystemreferanse(vsr, lokasjon.kommuner?.[0]))
                  .filter(
                      (
                          vegsystemreferanse
                      ): vegsystemreferanse is NonNullable<typeof vegsystemreferanse> =>
                          vegsystemreferanse !== null
                  )
            : [],
        lokasjon.stedfestinger
            ? lokasjon.stedfestinger.flatMap(transformVegobjektStedfesting)
            : [],
        lokasjon.geometri ? transformGeometri(lokasjon.geometri) : null,
        lokasjon.lengde
    );
}

function transformRelasjoner(relasjoner: ApiRelasjoner | undefined): VegobjektRelasjoner {
    return new VegobjektRelasjoner(
        relasjoner?.barn
            ? relasjoner.barn.map(b => {
                  const relation = b as ApiRelasjonMedVegobjekter;
                  return new VegobjektRelasjon(
                      relation.type,
                      relation.vegobjekter.map(transformVegobjekt),
                      Relasjonstype.Datter
                  );
              })
            : [],
        relasjoner?.foreldre
            ? relasjoner.foreldre.map(
                  f =>
                      new VegobjektRelasjon(
                          f.type,
                          f.vegobjekter.map(transformVegobjekt),
                          Relasjonstype.Mor
                      )
              )
            : []
    );
}
type SosiDetaljniva = 'Kjørebane' | 'Kjørefelt' | 'Vegtrase' | 'Vegtrase og kjørebane';

function transformDetaljniva(
    detaljniva: SosiDetaljniva
): Detaljniva | DetaljnivaAdditional {
    switch (detaljniva) {
        case 'Kjørebane':
            return Detaljniva.KJOREBANE;
        case 'Kjørefelt':
            return Detaljniva.KJOREFELT;
        case 'Vegtrase':
            return Detaljniva.VEGTRASE;
        case 'Vegtrase og kjørebane':
            return DetaljnivaAdditional.VEGTRASE_OG_KJOREBANE;
        default:
            throw new Error(`Unknown detaljnivå: ${detaljniva}`);
    }
}
function transformSegment(segment: ApiVegsegment): Vegsegment {
    const position = segment.relativPosisjon;
    const startPosition = segment.startposisjon ?? position;
    const endPosition = segment.sluttposisjon ?? position;
    if (startPosition === undefined || endPosition === undefined) {
        throw new Error('Vegsegment mangler posisjon');
    }

    return new Vegsegment(
        segment.veglenkesekvensid,
        startPosition,
        endPosition,
        transformDetaljniva(segment.detaljnivå),
        transformGeometri(segment.geometri),
        segment.feltoversikt ?? []
    );
}

function transformSegmenter(segmenter: ApiVegobjekt['vegsegmenter']): Vegsegment[] {
    if (!segmenter || segmenter.length === 0) return [];
    return segmenter.map(transformSegment);
}
